import apiClient, { extractData } from "./client";
import {
  attendanceRecordDateKey,
  utcCalendarDateKey,
} from "../utils/attendanceTimeFormat";
import { todayDateOnlyIso } from "../utils/currentLeaveHelpers";

/** Remote / hybrid app check-in */
export async function appCheckIn(data = {}) {
  const response = await apiClient.post("/attendance/app-checkin", data);
  return extractData(response);
}

/** Remote / hybrid app break-out (4-punch schedules) */
export async function appBreakOut(data = {}) {
  const response = await apiClient.post("/attendance/app-break-out", data);
  return extractData(response);
}

/** Remote / hybrid app break-in (4-punch schedules) */
export async function appBreakIn(data = {}) {
  const response = await apiClient.post("/attendance/app-break-in", data);
  return extractData(response);
}

/** Remote / hybrid app check-out */
export async function appCheckOut(data = {}) {
  const response = await apiClient.post("/attendance/app-checkout", data);
  return extractData(response);
}

/** Legacy aliases — backend routes these through app check-in/out */
export async function checkIn(data = {}) {
  return appCheckIn(data);
}

export async function checkOut(data = {}) {
  return appCheckOut(data);
}

export async function getMyAttendance(params = {}) {
  const response = await apiClient.get("/attendance/my", { params });
  return extractData(response);
}

export async function getAttendanceReport(params = {}) {
  const response = await apiClient.get("/attendance/report", { params });
  return extractData(response);
}

export async function getAppAttendanceStatus() {
  const response = await apiClient.get("/attendance/app-status");
  return extractData(response);
}

/** Tomorrow + day-after shifts for employee home */
export async function getMyUpcomingShifts() {
  const response = await apiClient.get("/attendance/my-upcoming-shifts");
  return extractData(response);
}

/** Live board. scope=team is the signed-in manager's people. */
export async function getLiveAttendanceStatus(params = {}) {
  const response = await apiClient.get("/attendance/live-status", { params });
  return extractData(response);
}

/** Global office / warehouse / cashier hours + custom named presets */
export async function getShiftHoursConfig() {
  const response = await apiClient.get("/attendance/shift-hours-config");
  return extractData(response);
}

/** Partial update of global shift hours config */
export async function updateShiftHoursConfig(data) {
  const response = await apiClient.put("/attendance/shift-hours-config", data);
  return extractData(response);
}

/**
 * Current punch for the home clock / app attendance page.
 * Prefers an open overnight session when present.
 */
export async function getTodayAttendance() {
  try {
    const status = await getAppAttendanceStatus();
    const current = status?.openSession || status?.today || null;
    const expectedPunchCount =
      current?.expectedPunchCount ?? status?.expectedPunchCount ?? null;
    return {
      today: current
        ? {
            ...current,
            expectedPunchCount:
              expectedPunchCount ?? current.expectedPunchCount,
          }
        : null,
      records: current ? [current] : [],
      handover: status?.handover || null,
      expectedPunchCount,
    };
  } catch {
    const now = new Date();
    const data = await getMyAttendance({
      month: now.getMonth() + 1,
      year: now.getFullYear(),
    });
    const records = data?.records || data || [];
    const utcKey = utcCalendarDateKey(now);
    const cairoKey = todayDateOnlyIso(now);
    const today = Array.isArray(records)
      ? records.find((r) => {
          const key = attendanceRecordDateKey(r.date);
          return key === utcKey || key === cairoKey;
        })
      : null;
    return {
      today: today || null,
      records: Array.isArray(records) ? records : [],
      handover: null,
      expectedPunchCount: today?.expectedPunchCount ?? null,
    };
  }
}
